import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useSession, useSupabaseClient } from '@supabase/auth-helpers-react';
import Table from '../../components/Table';
import Pagination from '../../components/Pagination';
import SearchableClientDropdown from '../../components/SearchableClientDropdown';
import SelectionActionBar from '../../components/SelectionActionBar';
import { useUserRole } from '../../utils/useUserRole';
import { ColumnDef, RowSelectionState } from '@tanstack/react-table';
import { pdf } from '@react-pdf/renderer';
import { 
  invoiceColumns, 
  Invoice,
  DEFAULT_PAGE_SIZE,
  SEARCH_DEBOUNCE_DELAY,
  DATE_RANGE_PRESETS,
  getInvoiceDateRange,
  formatCurrency,
  formatDate,
  buildFilterSummary,
  JOB_TYPE_OPTIONS
} from './config';
import InvoiceColumn from './components/InvoiceColumn';
import Dropdown from '../../components/Dropdown';
import { DropdownItem } from '../../components/Dropdown/config';
import AddInvoiceModal from './components/AddInvoiceModal';
import InvoiceDetailModal from './components/InvoiceDetailModal';
import BulkStatusChangeModal from './components/BulkStatusChangeModal';
import BulkRecordedDateModal from './components/BulkRecordedDateModal';
import InvoiceExportPDF from './components/InvoiceExportPDF';
import InvoiceEditor from './components/InvoiceEditor';
import { useServerFiltering } from '../../utils/useServerFiltering';
import { useUrlFilters, INVOICE_FILTER_DEFINITIONS } from '../../utils/useUrlFilters';
import TableSkeleton from '../../components/TableSkeleton';
import TextInput from '../../components/TextInput';
import StatusPill, { PillTone } from '../../components/StatusPill';
import { PlusIcon } from '../../components/IconButton/icons';
import ListPage, { FilterTrigger, SearchInput, StatusSegments, useNarrowViewport, useCompactTable } from '../../components/ListPage';

// The register's status colours in the pill's vocabulary: Sent is the
// success and Entered the step after it (the same pair as a document's
// Signed -> Sent).
const invoiceStatusTone: Record<string, PillTone> = {
  Draft: 'warning',
  Sent: 'success',
  Entered: 'complete',
};

// =======================================
// INTERFACES
// =======================================

interface TeamMember {
  id: number | string;
  first_name: string;
  last_name: string;
  type?: string;
}

interface WorkflowStatus {
  id: number;
  status_name: string;
  job_types: string[];
}

// =======================================
// MAIN COMPONENT
// =======================================

const InvoiceRegister: React.FC = () => {
  const { invoiceId } = useParams<{ invoiceId: string }>();

  // If we have an invoiceId param, render the full-page editor
  if (invoiceId) {
    return (
      <div className="invoice-editor-fullscreen">
        <InvoiceEditor />
      </div>
    );
  }

  return <InvoiceRegisterList />;
};

const InvoiceRegisterList: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  // Authentication hooks
  const { isReadOnly } = useUserRole();
  const session = useSession();
  const supabase = useSupabaseClient();

  // UI State
  const [staticDataLoading, setStaticDataLoading] = useState(true);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [workflowStatuses, setWorkflowStatuses] = useState<WorkflowStatus[]>([]);

  // Modal states
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
  const [isBulkStatusModalOpen, setIsBulkStatusModalOpen] = useState(false);
  const [isBulkRecordedDateModalOpen, setIsBulkRecordedDateModalOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  // Row selection state
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [isAllFilteredSelected, setIsAllFilteredSelected] = useState(false);

  // URL-synced filter state - persists across navigation
  const { 
    filters: urlFilters, 
    setFilter, 
    setFilters: setUrlFilters,
    resetFilters: resetUrlFilters
  } = useUrlFilters(INVOICE_FILTER_DEFINITIONS);

  // Derive filter values from URL state
  const searchInput = urlFilters.search;
  const filterStatus = urlFilters.status;
  // Memoize filterClient to prevent unnecessary re-renders
  const filterClient = useMemo(() => 
    urlFilters.client ? [urlFilters.client] : [], 
    [urlFilters.client]
  );
  const dateRangePreset = urlFilters.dateRange;
  const selectedTypes = urlFilters.types;
  const selectedProjectStatus = urlFilters.projectStatus;
  const filterFromInvoice = urlFilters.fromInvoice;
  const filterToInvoice = urlFilters.toInvoice;
  const urlPage = urlFilters.page;
  const urlPageSize = urlFilters.pageSize;

  // Determine if custom date range is active
  const customDateRange = dateRangePreset === 'custom';

  // Calculate actual date values (from preset or custom)
  const { filterFromDate, filterToDate } = useMemo(() => {
    if (customDateRange) {
      // Use custom dates from URL
      return {
        filterFromDate: urlFilters.fromDate,
        filterToDate: urlFilters.toDate
      };
    }
    // Calculate dates from preset
    const dateRange = getInvoiceDateRange(dateRangePreset);
    return {
      filterFromDate: dateRange.from,
      filterToDate: dateRange.to
    };
  }, [customDateRange, dateRangePreset, urlFilters.fromDate, urlFilters.toDate]);

  // Initial filters captured from URL on first mount so the first fetch already
  // has filters applied — avoids the "list flickers from unfiltered to filtered"
  // pattern when returning to the register from a filtered URL.
  const initialFiltersRef = useRef<Record<string, unknown> | null>(null);
  if (initialFiltersRef.current === null) {
    const pageSize = urlPageSize || DEFAULT_PAGE_SIZE;
    initialFiltersRef.current = {
      search_term: searchInput || null,
      status_filter: filterStatus !== 'All' ? [filterStatus] : null,
      client_filter: filterClient.length > 0 ? filterClient[0] : null,
      from_date: filterFromDate || null,
      to_date: filterToDate || null,
      types_filter: selectedTypes.length > 0 ? selectedTypes : null,
      project_status_filter: selectedProjectStatus.length > 0 ? selectedProjectStatus : null,
      from_invoice_number: filterFromInvoice ? parseInt(filterFromInvoice, 10) : null,
      to_invoice_number: filterToInvoice ? parseInt(filterToInvoice, 10) : null,
      page_size: pageSize,
      page_offset: ((urlPage || 1) - 1) * pageSize,
      sort_by: 'invoice_number',
      sort_order: 'desc',
    };
  }

  // Server-side filtering hook (now with TanStack Query caching)
  const {
    data: invoices,
    pagination,
    summary,
    loading,
    filtering,
    error,
    setFilters,
    refetch
  } = useServerFiltering<Invoice>({
    rpcFunction: 'get_filtered_invoices',
    defaultFilters: initialFiltersRef.current,
    debounceDelay: SEARCH_DEBOUNCE_DELAY
  });

  // Sync URL filter state with server filtering
  useEffect(() => {
    const pageSize = urlPageSize || DEFAULT_PAGE_SIZE;
    const pageOffset = ((urlPage || 1) - 1) * pageSize;
    
    setFilters({
      search_term: searchInput || null,
      status_filter: filterStatus !== 'All' ? [filterStatus] : null,
      client_filter: filterClient.length > 0 ? filterClient[0] : null,
      from_date: filterFromDate || null,
      to_date: filterToDate || null,
      types_filter: selectedTypes.length > 0 ? selectedTypes : null,
      project_status_filter: selectedProjectStatus.length > 0 ? selectedProjectStatus : null,
      from_invoice_number: filterFromInvoice ? parseInt(filterFromInvoice, 10) : null,
      to_invoice_number: filterToInvoice ? parseInt(filterToInvoice, 10) : null,
      page_size: pageSize,
      page_offset: pageOffset
    });
  }, [
    searchInput, filterStatus, filterClient, filterFromDate, filterToDate, 
    selectedTypes, selectedProjectStatus, filterFromInvoice, filterToInvoice,
    urlPage, urlPageSize, setFilters
  ]);

  // Clear selection when filters or page changes
  useEffect(() => {
    setRowSelection({});
    setIsAllFilteredSelected(false);
  }, [searchInput, filterStatus, filterClient, filterFromDate, filterToDate, selectedTypes, selectedProjectStatus, filterFromInvoice, filterToInvoice, urlPage]);

  // Clear invalid project status selections when types filter changes
  useEffect(() => {
    if (selectedTypes.length > 0 && selectedProjectStatus.length > 0) {
      // Get valid statuses for selected types
      const validStatuses = workflowStatuses
        .filter(status => status.job_types.some(jobType => selectedTypes.includes(jobType)))
        .map(status => status.status_name);
      
      // Remove any selected statuses that are no longer valid
      const stillValidStatuses = selectedProjectStatus.filter(s => validStatuses.includes(s));
      if (stillValidStatuses.length !== selectedProjectStatus.length) {
        setFilter('projectStatus', stillValidStatuses);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTypes, workflowStatuses]); // Don't include selectedProjectStatus to avoid infinite loop

  // Check if any filters are active (for UI purposes)
  const hasActiveFilters = 
    searchInput.length > 0 ||
    filterStatus !== 'All' ||
    filterClient.length > 0 ||
    selectedTypes.length > 0 ||
    selectedProjectStatus.length > 0 ||
    filterFromInvoice.length > 0 ||
    filterToInvoice.length > 0 ||
    (dateRangePreset !== 'allTime' && dateRangePreset !== 'thisMonth') ||
    customDateRange;

  // Calculate selected count
  const selectedIds = useMemo(() => {
    return Object.keys(rowSelection).filter(key => rowSelection[key]);
  }, [rowSelection]);

  const selectedCount = useMemo(() => {
    if (isAllFilteredSelected) {
      return pagination?.total_records || 0;
    }
    return selectedIds.length;
  }, [isAllFilteredSelected, selectedIds.length, pagination?.total_records]);

  // Handle search input change
  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setUrlFilters({ search: e.target.value, page: 1 }); // Reset to page 1 on search
  }, [setUrlFilters]);

  // Handle status filter change
  const handleStatusChange = useCallback((status: string) => {
    setUrlFilters({ status, page: 1 });
  }, [setUrlFilters]);

  const handleClearSearch = useCallback(() => {
    setUrlFilters({ search: '', page: 1 });
  }, [setUrlFilters]);

  const isNarrow = useNarrowViewport();
  // Below ~1366px the twelve columns do not fit, so Sent and Recorded merge
  // into one column: the sent date, with the recorded date beneath it in
  // small type when it differs. Better that than a sideways scroll.
  const compactTable = useCompactTable();

  // Handle date range preset change
  const handleDateRangeChange = useCallback((value: string | string[]) => {
    const preset = Array.isArray(value) ? value[0] : value;
    
    if (preset === 'custom') {
      // Switch to custom mode, initialize with current month dates
      const currentDates = getInvoiceDateRange('thisMonth');
      setUrlFilters({ 
        dateRange: 'custom',
        fromDate: currentDates.from,
        toDate: currentDates.to,
        page: 1
      });
    } else {
      // Clear custom dates and set preset
      setUrlFilters({ 
        dateRange: preset,
        fromDate: '',
        toDate: '',
        page: 1
      });
    }
  }, [setUrlFilters]);

  // Handle custom date changes
  const handleFromDateChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setFilter('fromDate', event.target.value);
  }, [setFilter]);

  const handleToDateChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setFilter('toDate', event.target.value);
  }, [setFilter]);

  // Handle types filter change
  const handleTypesChange = useCallback((types: string[]) => {
    setUrlFilters({ types, page: 1 });
  }, [setUrlFilters]);

  // Handle project status filter change
  const handleProjectStatusChange = useCallback((statuses: string[]) => {
    setUrlFilters({ projectStatus: statuses, page: 1 });
  }, [setUrlFilters]);

  // Handle client filter change
  const handleClientChange = useCallback((clientId: string | number | null) => {
    if (clientId === null || clientId === 'none') {
      setUrlFilters({ client: '', page: 1 });
    } else {
      setUrlFilters({ client: clientId.toString(), page: 1 });
    }
  }, [setUrlFilters]);

  // Handle invoice number range changes
  const handleFromInvoiceChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setUrlFilters({ fromInvoice: e.target.value, page: 1 });
  }, [setUrlFilters]);

  const handleToInvoiceChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setUrlFilters({ toInvoice: e.target.value, page: 1 });
  }, [setUrlFilters]);

  // Clear all filters
  const handleClearFilters = useCallback(() => {
    resetUrlFilters();
  }, [resetUrlFilters]);

  // Fetch static data
  useEffect(() => {
    const fetchStaticData = async () => {
      if (!session?.user) return;

      try {
        setStaticDataLoading(true);

        // Fetch active team members only
        const { data: teamMembersData, error: teamMembersError } = await supabase
          .from('active_team_members')
          .select('id, first_name, last_name, type')
          .order('first_name', { ascending: true });

        if (teamMembersError) {
          console.error('Error fetching team members:', teamMembersError);
        } else {
          setTeamMembers(teamMembersData || []);
        }

        // Fetch all workflow statuses with their job types
        const { data: statusData, error: statusError } = await supabase
          .rpc('get_all_workflow_statuses');

        if (statusError) {
          console.error('Error fetching workflow statuses:', statusError);
        } else {
          setWorkflowStatuses(statusData || []);
        }

      } catch (error) {
        console.error('Error in fetchStaticData:', error);
      } finally {
        setStaticDataLoading(false);
      }
    };

    fetchStaticData();
  }, [session, supabase]);

  // Handle row click - open detail modal
  const handleRowClick = useCallback((invoiceId: number) => {
    const invoice = invoices.find(p => p.id === invoiceId);
    if (invoice) {
      setSelectedInvoice(invoice);
      setIsDetailModalOpen(true);
    }
  }, [invoices]);

  // Handle opening the invoice editor
  const handleOpenEditor = useCallback((invoiceId: number) => {
    navigate(`/invoices/${invoiceId}`, { state: { fromSearch: location.search } });
  }, [navigate, location.search]);

  // Handle invoice creation
  const handleInvoiceCreated = useCallback(() => {
    refetch();
  }, [refetch]);

  // Handle invoice update
  const handleInvoiceUpdated = useCallback(() => {
    refetch();
    setIsDetailModalOpen(false);
    setSelectedInvoice(null);
  }, [refetch]);

  // Handle invoice deletion
  const handleInvoiceDeleted = useCallback(() => {
    refetch();
    setIsDetailModalOpen(false);
    setSelectedInvoice(null);
  }, [refetch]);

  // Pagination handlers - update URL
  const handlePageChange = useCallback((page: number) => {
    setFilter('page', page);
  }, [setFilter]);

  const handlePageSizeChange = useCallback((newPageSize: number) => {
    setUrlFilters({ pageSize: newPageSize, page: 1 });
  }, [setUrlFilters]);

  // Row selection handlers
  const handleSelectPage = useCallback(() => {
    const newSelection: RowSelectionState = {};
    invoices.forEach((invoice) => {
      newSelection[invoice.id.toString()] = true;
    });
    setRowSelection(newSelection);
    setIsAllFilteredSelected(false);
  }, [invoices]);

  const handleSelectAllFiltered = useCallback(() => {
    // Select all on current page visually, but flag that ALL filtered are selected
    const newSelection: RowSelectionState = {};
    invoices.forEach((invoice) => {
      newSelection[invoice.id.toString()] = true;
    });
    setRowSelection(newSelection);
    setIsAllFilteredSelected(true);
  }, [invoices]);

  const handleClearSelection = useCallback(() => {
    setRowSelection({});
    setIsAllFilteredSelected(false);
  }, []);

  // Bulk status update handler
  const handleBulkStatusUpdate = useCallback(async (newStatus: string) => {
    if (isAllFilteredSelected) {
      // Update all filtered invoices using filter-based RPC
      const { data, error } = await supabase.rpc('bulk_update_invoice_status_by_filter', {
        new_status: newStatus,
        search_term: searchInput || null,
        status_filter: filterStatus !== 'All' ? [filterStatus] : null,
        client_filter: filterClient.length > 0 ? filterClient[0] : null,
        from_date: filterFromDate || null,
        to_date: filterToDate || null,
        types_filter: selectedTypes.length > 0 ? selectedTypes : null,
        project_status_filter: selectedProjectStatus.length > 0 ? selectedProjectStatus : null,
        from_invoice_number: filterFromInvoice ? parseInt(filterFromInvoice, 10) : null,
        to_invoice_number: filterToInvoice ? parseInt(filterToInvoice, 10) : null
      });

      if (error) {
        throw error;
      }

      console.log('Bulk update result:', data);
    } else {
      // Update specific invoice IDs - selectedIds contains the actual invoice IDs (as strings)
      const invoiceIds = selectedIds.map(id => parseInt(id));
      
      const { data, error } = await supabase.rpc('bulk_update_invoice_status', {
        invoice_ids: invoiceIds,
        new_status: newStatus
      });

      if (error) {
        throw error;
      }

      console.log('Bulk update result:', data);
    }

    // Clear selection and refresh
    handleClearSelection();
    refetch();
  }, [
    isAllFilteredSelected,
    supabase,
    searchInput,
    filterStatus,
    filterClient,
    filterFromDate,
    filterToDate,
    selectedTypes,
    selectedProjectStatus,
    filterFromInvoice,
    filterToInvoice,
    selectedIds,
    handleClearSelection,
    refetch
  ]);

  const handleBulkRecordedDateUpdate = useCallback(async (newRecordedDate: string) => {
    if (isAllFilteredSelected) {
      // Update all filtered invoices using filter-based RPC
      const { error } = await supabase.rpc('bulk_update_invoice_recorded_date_by_filter', {
        new_recorded_date: newRecordedDate,
        search_term: searchInput || null,
        status_filter: filterStatus !== 'All' ? [filterStatus] : null,
        client_filter: filterClient.length > 0 ? filterClient[0] : null,
        from_date: filterFromDate || null,
        to_date: filterToDate || null,
        types_filter: selectedTypes.length > 0 ? selectedTypes : null,
        project_status_filter: selectedProjectStatus.length > 0 ? selectedProjectStatus : null,
        from_invoice_number: filterFromInvoice ? parseInt(filterFromInvoice, 10) : null,
        to_invoice_number: filterToInvoice ? parseInt(filterToInvoice, 10) : null
      });

      if (error) {
        throw error;
      }
    } else {
      // Update specific invoice IDs
      const invoiceIds = selectedIds.map(id => parseInt(id));

      const { error } = await supabase.rpc('bulk_update_invoice_recorded_date', {
        invoice_ids: invoiceIds,
        new_recorded_date: newRecordedDate
      });

      if (error) {
        throw error;
      }
    }

    // Clear selection and refresh
    handleClearSelection();
    refetch();
  }, [
    isAllFilteredSelected,
    supabase,
    searchInput,
    filterStatus,
    filterClient,
    filterFromDate,
    filterToDate,
    selectedTypes,
    selectedProjectStatus,
    filterFromInvoice,
    filterToInvoice,
    selectedIds,
    handleClearSelection,
    refetch
  ]);

  // PDF export handler
  const handleExportPDF = useCallback(async () => {
    try {
      setIsExporting(true);

      let exportInvoices: Invoice[];

      if (isAllFilteredSelected) {
        const totalCount = pagination?.total_records || 0;
        const { data: response, error: rpcError } = await supabase.rpc('get_filtered_invoices', {
          search_term: searchInput || null,
          status_filter: filterStatus !== 'All' ? [filterStatus] : null,
          client_filter: filterClient.length > 0 ? filterClient[0] : null,
          from_date: filterFromDate || null,
          to_date: filterToDate || null,
          types_filter: selectedTypes.length > 0 ? selectedTypes : null,
          project_status_filter: selectedProjectStatus.length > 0 ? selectedProjectStatus : null,
          from_invoice_number: filterFromInvoice ? parseInt(filterFromInvoice, 10) : null,
          to_invoice_number: filterToInvoice ? parseInt(filterToInvoice, 10) : null,
          page_size: totalCount,
          page_offset: 0,
          sort_by: 'invoice_number',
          sort_order: 'desc'
        });

        if (rpcError) throw rpcError;
        exportInvoices = response?.invoices || [];
      } else {
        const selectedIdSet = new Set(selectedIds);
        exportInvoices = invoices.filter(inv => selectedIdSet.has(inv.id.toString()));
      }

      if (exportInvoices.length === 0) return;

      // Resolve client name for filter summary
      let clientName: string | null = null;
      if (filterClient.length > 0 && exportInvoices.length > 0) {
        clientName = exportInvoices[0].client_name;
      }

      const filterSummary = buildFilterSummary({
        filterStatus,
        clientName,
        dateRangePreset,
        filterFromDate,
        filterToDate,
        selectedTypes,
        selectedProjectStatus,
        filterFromInvoice,
        filterToInvoice,
        searchInput,
      });

      const totals = exportInvoices.reduce(
        (acc, inv) => ({
          ourFee: acc.ourFee + inv.our_fee,
          disbursements: acc.disbursements + inv.disbursements,
          totalAmount: acc.totalAmount + inv.total_amount,
        }),
        { ourFee: 0, disbursements: 0, totalAmount: 0 }
      );

      const now = new Date();
      const generatedDate = now.toLocaleDateString('en-AU', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      });

      const blob = await pdf(
        <InvoiceExportPDF
          invoices={exportInvoices}
          filterSummary={filterSummary}
          totals={totals}
          generatedDate={generatedDate}
          invoiceCount={exportInvoices.length}
        />
      ).toBlob();

      const dateStr = now.toISOString().slice(0, 10);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `invoice-register-export-${dateStr}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error exporting PDF:', err);
    } finally {
      setIsExporting(false);
    }
  }, [
    isAllFilteredSelected,
    pagination?.total_records,
    supabase,
    searchInput,
    filterStatus,
    filterClient,
    filterFromDate,
    filterToDate,
    selectedTypes,
    selectedProjectStatus,
    filterFromInvoice,
    filterToInvoice,
    selectedIds,
    invoices,
    dateRangePreset,
  ]);

  // Status radio options with counts
  const statusOptions = useMemo(() => [
    { value: 'All', label: 'All', count: summary?.total_count || 0 },
    { value: 'Draft', label: 'Draft', count: summary?.draft_count || 0 },
    { value: 'Sent', label: 'Sent', count: summary?.sent_count || 0 },
    { value: 'Entered', label: 'Entered', count: summary?.entered_count || 0 }
  ], [summary]);

  // Create dropdown items for date range presets
  const dateRangeDropdownItems: DropdownItem[] = useMemo(() => 
    DATE_RANGE_PRESETS.map(preset => ({
      id: preset.value,
      label: preset.label,
      value: preset.value
    })), []
  );

  // Create dropdown items for job types
  const jobTypeItems: DropdownItem[] = useMemo(() => 
    JOB_TYPE_OPTIONS.map(type => ({
      id: type,
      label: type,
      value: type
    })), []
  );

  // Create dropdown items for project status - dynamic based on selected job types
  const projectStatusItems: DropdownItem[] = useMemo(() => {
    // Filter statuses based on selected job types
    let filteredStatuses = workflowStatuses;
    
    if (selectedTypes.length > 0) {
      // Show only statuses that belong to at least one selected job type
      filteredStatuses = workflowStatuses.filter(status =>
        status.job_types.some(jobType => selectedTypes.includes(jobType))
      );
    }
    
    return filteredStatuses.map(status => ({
      id: status.status_name,
      label: status.status_name,
      value: status.status_name
    }));
  }, [workflowStatuses, selectedTypes]);


  // Generate table columns with flexible width configuration
  const columns = useMemo<ColumnDef<Invoice>[]>(() => {
    // Column budget (table-layout: fixed on desktop; see the Documents
    // register). Eleven columns plus the select box, so the page uses the
    // shell's --tight density (10px inner padding) and the fixed columns
    // come to ~990px. Client takes 14% and Description the remainder;
    // both carry hover tooltips. Money is right-aligned so the figures line
    // up under each other. Inv # and Job # are ids, so they never ellipsise:
    // 84px leaves 64px of text, room for production's five-digit
    // "TE12345" (58px at 14px) and a stage job number like "12345.12"
    // (59px). At 72px both were clipped.
    const columnConfig: Record<string, { minWidth?: string; width?: string; maxWidth?: string; align?: 'left' | 'right' | 'center' }> = {
      invoice_number: { minWidth: '84px', width: '84px' },
      job_number: { minWidth: '84px', width: '84px' },
      invoice_date: { minWidth: '100px', width: '100px' },
      recorded_date: { minWidth: '104px', width: '104px' },
      client_name: { minWidth: '150px', width: '14%' },
      project_description: { minWidth: '150px', width: 'auto' },
      our_fee: { minWidth: '108px', width: '108px', align: 'right' },
      disbursements: { minWidth: '108px', width: '108px', align: 'right' },
      total_amount: { minWidth: '112px', width: '112px', align: 'right' },
      status: { minWidth: '92px', width: '92px' }
    };

    const cols: ColumnDef<Invoice>[] = Object.entries(invoiceColumns)
      .filter(([key]) => !(compactTable && key === 'recorded_date'))
      .map(([key, config]) => ({
      accessorKey: key,
      header: key === 'invoice_date' && compactTable ? 'Sent / Rec.' : config.header,
      enableSorting: config.sortable,
      meta: key === 'invoice_date' && compactTable ? { minWidth: '112px', width: '112px' } : columnConfig[key],
      cell: ({ row }) => {
        const value = row.getValue(key);
        
        switch (config.component) {
          case 'DateColumn':
            if (key === 'invoice_date' && compactTable) {
              const sent = formatDate(value as string);
              const recorded = row.original.recorded_date ? formatDate(row.original.recorded_date) : null;
              return (
                <span className="date-pair" title={recorded ? `Sent ${sent}, recorded ${recorded}` : `Sent ${sent}`}>
                  {sent}
                  {recorded && recorded !== sent && <small className="date-pair__second">rec. {recorded}</small>}
                </span>
              );
            }
            return <span>{formatDate(value as string)}</span>;
          case 'CurrencyColumn':
            return <span>{formatCurrency(value as number)}</span>;
          case 'StatusColumn':
            return <StatusPill label={value as string} tone={invoiceStatusTone[value as string] ?? 'neutral'} />;
          default:
            return <InvoiceColumn config={config} row={row.original} />;
        }
      }
    }));

    cols.push({
      id: 'actions',
      header: '',
      enableSorting: false,
      // 24px right gutter + 10px + the widest pill (icon + "Tax Invoice").
      meta: { minWidth: '148px', width: '148px' },
      cell: ({ row }) => {
        const hasLineItems = row.original.line_item_count > 0;
        const bundledIntoId = row.original.bundled_in_invoice_id;
        if (bundledIntoId) {
          return (
            <button
              className="tax-invoice-pill tax-invoice-pill--bundled"
              onClick={(e) => {
                e.stopPropagation();
                handleOpenEditor(bundledIntoId);
              }}
              title="Open the invoice this is bundled into"
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>
              Bundled
            </button>
          );
        }
        // Modal-only invoices that have already been Sent/Entered are
        // historical records where `our_fee` is the canonical billed total.
        // Building a tax invoice document for them is almost always a
        // mistake — the edit lock would block any write anyway, but
        // disabling the entry point avoids the confusing "locked" banner.
        // To rebuild the document, revert status to Draft first.
        const isLockedModalOnly = !hasLineItems && row.original.status !== 'Draft';
        if (isLockedModalOnly) {
          return (
            <button
              className="tax-invoice-pill tax-invoice-pill--locked"
              disabled
              title={`Revert to Draft to build a tax invoice (currently ${row.original.status})`}
              onClick={(e) => e.stopPropagation()}
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
              Tax Invoice
            </button>
          );
        }
        // With line items the action depends on status: Draft → edit (pencil),
        // Sent/Entered → view (eye), since the document is read-only.
        const isViewOnly = hasLineItems && row.original.status !== 'Draft';
        const variantClass = !hasLineItems
          ? 'tax-invoice-pill--create'
          : isViewOnly
            ? 'tax-invoice-pill--view'
            : 'tax-invoice-pill--edit';
        const variantTitle = !hasLineItems
          ? 'Create tax invoice'
          : isViewOnly
            ? 'View tax invoice (read-only)'
            : 'Edit tax invoice';
        return (
          <button
            className={`tax-invoice-pill ${variantClass}`}
            onClick={(e) => {
              e.stopPropagation();
              handleOpenEditor(row.original.id);
            }}
            title={variantTitle}
          >
            {!hasLineItems ? (
              <>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                Tax Invoice
              </>
            ) : isViewOnly ? (
              <>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                Tax Invoice
              </>
            ) : (
              <>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                Tax Invoice
              </>
            )}
          </button>
        );
      },
    });

    return cols;
  }, [handleOpenEditor, compactTable]);

  // Get row ID for selection tracking
  const getRowId = useCallback((row: Invoice) => row.id.toString(), []);

  // Current page from pagination (for display)
  const currentPage = pagination?.page || urlPage || 1;
  const currentPageSize = pagination?.page_size || urlPageSize || DEFAULT_PAGE_SIZE;

  return (
    <ListPage
      title="Invoice Register"
      className="invoice-register-page list-page--tight"
      loading={staticDataLoading}
      skeleton={{ segmentCount: 4, filterCount: 6, showAction: true }}
      search={
        <SearchInput
          placeholder="Search invoices, projects, clients…"
          value={searchInput}
          onChange={handleSearchChange}
          onClear={handleClearSearch}
          aria-label="Search invoices"
        />
      }
      actions={
        <button
          type="button"
          className="pill-btn pill-btn--solid pill-btn--sm"
          onClick={() => setIsAddModalOpen(true)}
          disabled={loading || isReadOnly}
        >
          <PlusIcon /> Add invoice
        </button>
      }
      segments={
        <StatusSegments
          aria-label="Invoice status"
          size={isNarrow ? 'sm' : 'md'}
          value={filterStatus}
          onChange={handleStatusChange}
          segments={statusOptions.map((o) => ({ value: o.value, label: o.label, count: o.count }))}
        />
      }
      filters={
        <>
          <FilterTrigger label="Inv # from" active={filterFromInvoice.length > 0}>
            <TextInput
              size="sm"
              type="number"
              min="1"
              value={filterFromInvoice}
              onChange={handleFromInvoiceChange}
              placeholder="Any"
              aria-label="Invoice number from"
              className="filter-trigger__number"
            />
          </FilterTrigger>
          <FilterTrigger label="to" active={filterToInvoice.length > 0}>
            <TextInput
              size="sm"
              type="number"
              min="1"
              value={filterToInvoice}
              onChange={handleToInvoiceChange}
              placeholder="Any"
              aria-label="Invoice number to"
              className="filter-trigger__number"
            />
          </FilterTrigger>
          <FilterTrigger label="Type" active={selectedTypes.length > 0}>
            <Dropdown
              items={jobTypeItems}
              value={selectedTypes}
              onChange={handleTypesChange}
              placeholder="All"
              multiSelect
              searchable
            />
          </FilterTrigger>
          <FilterTrigger label="Project status" active={selectedProjectStatus.length > 0}>
            <Dropdown
              items={projectStatusItems}
              value={selectedProjectStatus}
              onChange={handleProjectStatusChange}
              placeholder="All"
              multiSelect
              searchable
            />
          </FilterTrigger>
          <FilterTrigger label="Client" active={filterClient.length > 0}>
            <SearchableClientDropdown
              value={filterClient.length > 0 ? filterClient[0] : null}
              onChange={(clientId: string | number | null) => handleClientChange(clientId)}
              placeholder="All"
              allowNone={true}
              noneLabel="All Clients"
            />
          </FilterTrigger>
          <FilterTrigger label="Date" active={dateRangePreset !== 'allTime'}>
            <Dropdown
              items={dateRangeDropdownItems}
              value={dateRangePreset}
              onChange={handleDateRangeChange}
              placeholder="All time"
              searchable={false}
            />
          </FilterTrigger>
          {customDateRange && (
            <>
              <FilterTrigger label="From" active>
                <TextInput size="sm" type="date" value={urlFilters.fromDate} onChange={handleFromDateChange} aria-label="From date" />
              </FilterTrigger>
              <FilterTrigger label="To" active>
                <TextInput size="sm" type="date" value={urlFilters.toDate} onChange={handleToDateChange} aria-label="To date" />
              </FilterTrigger>
            </>
          )}
        </>
      }
      hasActiveFilters={hasActiveFilters}
      onClearFilters={handleClearFilters}
      resultsText={
        !loading && pagination && (
          <>
            <span>
              Showing {pagination.total_records > 0 ? ((currentPage - 1) * currentPageSize) + 1 : 0}–{Math.min(currentPage * currentPageSize, pagination.total_records)} of {pagination.total_records.toLocaleString()} invoices
            </span>
            {summary && (
              <>
                <span className="list-page__stat">Our fee <b>{formatCurrency(summary.total_our_fee)}</b></span>
                <span className="list-page__stat">Disbursements <b>{formatCurrency(summary.total_disbursements)}</b></span>
                <span className="list-page__stat">Invoiced <b>{formatCurrency(summary.total_invoiced)}</b></span>
              </>
            )}
          </>
        )
      }
      footer={
        <Pagination
          variant="bar"
          currentPage={currentPage}
          totalPages={pagination?.total_pages ?? 1}
          totalRecords={pagination?.total_records ?? 0}
          pageSize={currentPageSize}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          hasNext={pagination?.has_next ?? false}
          hasPrevious={pagination?.has_previous ?? false}
          loading={loading || filtering}
          showPageInfo
          pageSizeOptions={[25, 50, 100]}
        />
      }
    >
      {error && (
        <div className="list-page__error">
          Error loading invoices: {typeof error === 'string' ? error : (error as any)?.message || 'Unknown error'}
        </div>
      )}

      {staticDataLoading || loading ? (
        <TableSkeleton compact rows={14} columns={11} showHeader />
      ) : invoices.length === 0 ? (
        <div className="list-page__empty">
          {hasActiveFilters ? 'No invoices match these filters.' : 'No invoices yet. Create your first invoice to get started.'}
        </div>
      ) : (
        <div className={`list-page__body ${filtering ? 'is-filtering' : ''}`.trim()}>
          <Table
            data={invoices}
            columns={columns}
            onRowClick={(row) => handleRowClick(row.id)}
            enableRowSelection={true}
            rowSelection={rowSelection}
            onRowSelectionChange={setRowSelection}
            getRowId={getRowId}
            stickyHeader
          />
        </div>
      )}

      {/* Selection Action Bar */}
      <SelectionActionBar
        selectedCount={selectedCount}
        itemLabel="invoice"
        onClearSelection={handleClearSelection}
        isVisible={selectedCount > 0}
        actions={[
          {
            label: isExporting ? 'Exporting...' : 'Export PDF',
            onClick: handleExportPDF,
            variant: 'secondary',
            disabled: isExporting
          },
          {
            label: 'Change Recorded Date',
            onClick: () => setIsBulkRecordedDateModalOpen(true),
            variant: 'secondary'
          },
          {
            label: 'Change Status',
            onClick: () => setIsBulkStatusModalOpen(true),
            variant: 'primary'
          }
        ]}
        totalFilteredCount={pagination?.total_records}
        onSelectPage={handleSelectPage}
        onSelectAll={handleSelectAllFiltered}
        currentPageCount={invoices.length}
        isAllSelected={isAllFilteredSelected}
      />

      {/* Modals */}
      <AddInvoiceModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onInvoiceCreated={handleInvoiceCreated}
        teamMembers={teamMembers}
      />

      <InvoiceDetailModal
        isOpen={isDetailModalOpen}
        onClose={() => {
          setIsDetailModalOpen(false);
          setSelectedInvoice(null);
        }}
        invoice={selectedInvoice}
        onInvoiceUpdated={handleInvoiceUpdated}
        onInvoiceDeleted={handleInvoiceDeleted}
        teamMembers={teamMembers}
      />

      <BulkStatusChangeModal
        isOpen={isBulkStatusModalOpen}
        onClose={() => setIsBulkStatusModalOpen(false)}
        selectedCount={selectedCount}
        onConfirm={handleBulkStatusUpdate}
        isAllFiltered={isAllFilteredSelected}
        totalFilteredCount={pagination?.total_records || 0}
      />

      <BulkRecordedDateModal
        isOpen={isBulkRecordedDateModalOpen}
        onClose={() => setIsBulkRecordedDateModalOpen(false)}
        selectedCount={selectedCount}
        onConfirm={handleBulkRecordedDateUpdate}
        isAllFiltered={isAllFilteredSelected}
        totalFilteredCount={pagination?.total_records || 0}
      />
    </ListPage>
  );
};

export default InvoiceRegister;
